import React from 'react';
import { add as dateAdd, endOfDay, format, startOfDay } from 'date-fns';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Toolbar,
} from '@mui/material';
import styled from '@emotion/styled';
import { spacing } from '@mui/system';
import { DatePicker, DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { Download as DownloadIcon } from '@mui/icons-material';

import TableFilterToolbar from '../../../components/tables/TableFilterToolbar';
import { filterStatuses, getInvoiceDisplayStatus } from '../utils/invoiceUtils';
import { verifyAuth } from '../../../apis/authApi';
import InvoiceTable from './InvoiceTable';
import { InvoiceListProps } from '../invoiceTypes';
import InvoiceCardList from './InvoiceCardList';

const Paper = styled(MuiPaper)(spacing);

const minDate = new Date(2010, 1, 1);

const baseUrl = `${window.location.protocol}//${window.location.hostname}/api`;

export interface InvoiceDateFilter {
  fromDate: Date;
  toDate: Date;
}

function InvoiceFilteredList({ isLoading, rows, hasBillingRole, backOfficeRootUrl }: InvoiceListProps) {
  const initFilterCriteria = () => ({ status: [], toDate: new Date(), fromDate: dateAdd(new Date(), { years: -1 }) });
  const [rowFilter, setRowFilter] = React.useState<{ status: string[]; fromDate: Date; toDate: Date }>(initFilterCriteria());

  const [fromDateError, setFromDateError] = React.useState<string | null>(null);
  const [toDateError, setToDateError] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState<boolean>(false);

  const formIsValid = React.useRef(true);

  const theme = useTheme();
  const isMultilineWidth = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const filteredRows = isLoading
    ? []
    : rows.filter(
        (row) =>
          (rowFilter.status.length == 0 || rowFilter.status.includes(getInvoiceDisplayStatus(row))) &&
          new Date(row.invoiceDate) >= startOfDay(rowFilter.fromDate) &&
          new Date(row.invoiceDate) <= endOfDay(rowFilter.toDate),
      );

  const getDateError = (error: DateValidationError, controlName?: string) => {
    if (error === 'disableFuture') {
      return 'Cannot be future date';
    }
    if (error === 'minDate') {
      if (controlName === 'toDate') {
        return 'Cannot be less than From date';
      }
      return 'Must be greater than 01/01/2010';
    }
    if (error === 'invalidDate') {
      return 'Date is not valid';
    }
    return null;
  };

  const handleStatusFilter = (filters: string[]) => {
    setRowFilter({ ...rowFilter, status: filters });
  };

  const handleDateFilter = async (ctx: PickerChangeHandlerContext<DateValidationError>, filter: Partial<InvoiceDateFilter>) => {
    const newFilter = { ...rowFilter, ...filter };

    formIsValid.current =
      !ctx.validationError &&
      newFilter.fromDate >= minDate &&
      newFilter.fromDate <= newFilter.toDate &&
      newFilter.fromDate <= new Date() &&
      newFilter.toDate >= minDate;

    setRowFilter(newFilter);
  };

  const getDownloadReportButtonTitle = () => {
    return formIsValid.current && filteredRows && filteredRows.length > 0
      ? `Download Billing Statement PDF\n${format(rowFilter.fromDate, 'MM/dd/yyyy')} - ${format(rowFilter.toDate, 'MM/dd/yyyy')}`
      : 'No billing data to download';
  };

  const handleDownloadBillingStatement = async () => {
    await verifyAuth();
    const popup = window.open(
      `${baseUrl}/billing-statement/pdf?BeginDate=${rowFilter.fromDate.toISOString()}&EndDate=${rowFilter.toDate.toISOString()}`,
      '_blank',
    );

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Paper>
      <Toolbar>
        <Grid container>
          <Grid item xs={12} lg={true} sx={{ mr: '14px' }}>
            <TableFilterToolbar options={filterStatuses} onFilterChange={handleStatusFilter} label="Invoice status" />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg="auto"
            xl="auto"
            sx={{
              marginTop: { xs: '-24px', lg: '0px' },
              marginLeft: { xs: '2px', lg: '0px' },
              marginBottom: {
                xs: '8px',
                sm: '-2px',
                md: '0px',
                lg: '0px',
              },
              textAlign: { sm: 'left', lg: 'right' },
              mr: '12px',
            }}
          >
            <DatePicker
              sx={{ mt: 6, ml: { xs: '2px', xl: '0px' }, width: { xs: 133, md: 170 } }}
              slotProps={{
                textField: {
                  size: isMultilineWidth ? 'small' : 'medium',
                  helperText: fromDateError,
                  error: !!fromDateError,
                  FormHelperTextProps: { sx: { marginLeft: 1, marginInlineStart: 1 } },
                },
              }}
              label="From date"
              value={rowFilter.fromDate}
              minDate={minDate}
              disableFuture={true}
              onError={(error) => {
                setFromDateError(getDateError(error));
              }}
              onChange={(val, ctx) => {
                handleDateFilter(ctx, { fromDate: val as Date });
              }}
            />
            <DatePicker
              sx={{ ml: '10px', mt: 6, width: { xs: 133, md: 170 } }}
              slotProps={{
                textField: {
                  size: isMultilineWidth ? 'small' : 'medium',
                  helperText: toDateError,
                  error: !!toDateError,
                  FormHelperTextProps: { sx: { marginLeft: 1, marginInlineStart: 1 } },
                },
              }}
              label="To date"
              value={rowFilter.toDate}
              minDate={rowFilter.fromDate ?? minDate}
              onError={(error) => {
                setToDateError(getDateError(error, 'toDate'));
              }}
              onChange={(val, ctx) => {
                handleDateFilter(ctx, { toDate: val as Date });
              }}
            />
            <div style={{ display: 'inline-block' }} title={getDownloadReportButtonTitle()}>
              <Box display={{ xs: 'none', sm: 'inline-block' }} mt={6} mb="16px" ml="10px">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  sx={{
                    pt: { sm: '6px', lg: '13px' },
                    pb: { sm: '6px', lg: '12px' },
                  }}
                  onClick={handleDownloadBillingStatement}
                  disabled={filteredRows.length == 0}
                  size={isMultilineWidth ? 'small' : 'large'}
                >
                  Billing Statement
                </Button>
              </Box>
              <Box position="relative" mt={6} ml={2} display={{ xs: 'inline-block', sm: 'none' }}>
                <IconButton
                  color="primary"
                  sx={{
                    padding: '7px',
                  }}
                  onClick={handleDownloadBillingStatement}
                  disabled={filteredRows.length == 0}
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
            </div>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider sx={{ mt: 1, mb: 0 }}></Divider>
      {isSmUp && <InvoiceTable isLoading={isLoading} rows={filteredRows} backOfficeRootUrl={backOfficeRootUrl} hasBillingRole={hasBillingRole} />}
      {!isSmUp && <InvoiceCardList isLoading={isLoading} rows={filteredRows} backOfficeRootUrl={backOfficeRootUrl} hasBillingRole={hasBillingRole} />}
      <Dialog open={open} onClose={handleClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'Pop-up Blocked'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            It looks like your browser blocked a pop-up we tried to open. To continue, please allow pop-ups for this site in your browser settings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default InvoiceFilteredList;
